import { get, post, patch, del } from '../../apiClient';
import { API_ENDPOINTS } from '../../config';

const ticketService = {
  /**
   * Create a new ticket
   */
  createTicket: async (ticketData) => {
    try {
      const response = await post(API_ENDPOINTS.CRM.TICKETS, ticketData);

      if (response && response.responseCode === 201) {
        return {
          success: true,
          ticket: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to create ticket',
          ticket: null
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to create ticket',
        ticket: null
      };
    }
  },

  /**
   * Get ticket by ID
   */
  getTicketById: async (ticketId) => {
    try {
      const response = await get(`${API_ENDPOINTS.CRM.TICKETS}/${ticketId}`);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          ticket: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve ticket',
          ticket: null
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve ticket',
        ticket: null
      };
    }
  },

  /**
   * Export a single ticket detail as a backend-generated Excel (.xlsx)
   * workbook and trigger the browser download.
   */
  exportTicketDetail: async (ticketId, fallbackName = 'Ticket') => {
    try {
      if (!ticketId) {
        return { success: false, error: 'Ticket ID is required' };
      }

      const blob = await get(`${API_ENDPOINTS.CRM.TICKETS}/${ticketId}/export`, {
        responseType: 'blob'
      });

      const sanitized = String(fallbackName || 'Ticket')
        .replace(/[\\/:*?"<>|]/g, '')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 80) || 'Ticket';
      const filename = `Ticket_${sanitized}.xlsx`;

      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to export ticket details'
      };
    }
  },

  /**
   * Get all tickets with pagination and search
   */
  getTickets: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.search) queryParams.append('search', filters.search);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.priority) queryParams.append('priority', filters.priority);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.assignee) queryParams.append('assignee', filters.assignee);

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.TICKETS}?${queryParams.toString()}`
        : API_ENDPOINTS.CRM.TICKETS;

      const response = await get(url);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          ...response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve tickets',
          data: [],
          totalItems: 0,
          page: 1,
          limit: 10,
          totalPages: 0
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve tickets',
        data: [],
        totalItems: 0,
        page: 1,
        limit: 10,
        totalPages: 0
      };
    }
  },

  /**
   * Export Tickets
   */
  exportTickets: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.search) queryParams.append('search', filters.search);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.priority) queryParams.append('priority', filters.priority);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.assignee) queryParams.append('assignee', filters.assignee);

      // Construct export URL
      const url = `${API_ENDPOINTS.CRM.TICKETS}/export?${queryParams.toString()}`;

      return {
        success: true,
        downloadUrl: url
      };
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to generate export URL'
      };
    }
  },

  /**
   * Get ticket KPIs
   */
  getTicketKPIs: async (status, assignee) => {
    try {
      const queryParams = new URLSearchParams();
      if (status) queryParams.append('status', status);
      if (assignee) queryParams.append('assignee', assignee);

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.TICKETS}/kpi?${queryParams.toString()}`
        : `${API_ENDPOINTS.CRM.TICKETS}/kpi`;

      const response = await get(url);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          ...response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve ticket KPIs',
          totalTickets: 0,
          openTickets: 0,
          inProgressTickets: 0,
          pendingTickets: 0,
          resolvedTickets: 0,
          closedTickets: 0
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve ticket KPIs',
        totalTickets: 0,
        openTickets: 0,
        inProgressTickets: 0,
        pendingTickets: 0,
        resolvedTickets: 0,
        closedTickets: 0
      };
    }
  },

  /**
   * Update ticket
   */
  updateTicket: async (ticketId, updateData) => {
    try {
      const response = await patch(`${API_ENDPOINTS.CRM.TICKETS}/${ticketId}`, updateData);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          ticket: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to update ticket',
          ticket: null
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to update ticket',
        ticket: null
      };
    }
  },

  /**
   * Add feedback to ticket
   */
  addFeedback: async (ticketId, feedbackData) => {
    try {
      const response = await post(`${API_ENDPOINTS.CRM.TICKETS}/${ticketId}/feedback`, feedbackData);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          ticket: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to add feedback',
          ticket: null
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to add feedback',
        ticket: null
      };
    }
  },

  /**
   * Delete ticket
   */
  deleteTicket: async (ticketId) => {
    try {
      const response = await del(`${API_ENDPOINTS.CRM.TICKETS}/${ticketId}`);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to delete ticket'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to delete ticket'
      };
    }
  },

  /**
   * Delete multiple tickets
   */
  bulkDeleteTickets: async (ticketIds) => {
    try {
      const response = await post(`${API_ENDPOINTS.CRM.TICKETS}/bulk-delete`, {
        ticketIds
      });

      if (response && response.responseCode === 200) {
        return {
          success: true,
          message: response.responseMessage || 'Tickets deleted successfully',
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to delete tickets'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to delete tickets'
      };
    }
  },

  /**
   * Preview bulk tickets from CSV data (backend validation)
   */
  bulkPreview: async (tickets) => {
    try {
      const response = await post(`${API_ENDPOINTS.CRM.TICKETS}/bulk-preview`, { tickets });
      if (response && response.responseCode === 200) {
        return { success: true, data: response.data };
      }
      return { success: false, error: response.responseMessage || 'Failed to generate preview' };
    } catch (error) {
      
      return { success: false, error: error.message || 'Failed to generate preview' };
    }
  },

  /**
   * Confirm and upload validated bulk tickets
   */
  bulkUpload: async (tickets) => {
    try {
      const response = await post(`${API_ENDPOINTS.CRM.TICKETS}/bulk-upload`, { tickets });
      if (response && response.responseCode === 201) {
        return { success: true, data: response.data };
      }
      return { success: false, error: response.responseMessage || 'Failed to upload tickets' };
    } catch (error) {
      
      return { success: false, error: error.message || 'Failed to upload tickets' };
    }
  }
};

export default ticketService;
